import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

// Pages
import EventLayout from "./Components/Events/EventLayout";
import EventDetail from "./Components/Events/landingPage/EventDetail";
import HackathonDashboard from "./Components/Events/HackathonDashboard";
import ApplicantDashboard from "./Components/Events/ApplicantDashboard";
import EventSideNav from "./Components/Events/Dashboard/EventSideNav";

// Admin Pages
import NewAdminDashboard from "./Components/Events/Dashboard/Admin/AdminDashboard";
import ManageApplications from "./Components/Events/Dashboard/Common/ManageApplications";
import UserPermissions from "./Components/Events/Dashboard/Admin/UserPermissions";
import CategoriesAndJudges from "./Components/Events/Dashboard/Admin/CategoriesAndJudges";
import CategoryDetails from "./Components/Events/Dashboard/Admin/CategoryDetails";
import TeamsManagement from "./Components/Events/Dashboard/Common/TeamsManagement";
import TeamDetails from "./Components/Events/Dashboard/Common/TeamDetails";
import ProjectsManagement from "./Components/Events/Dashboard/Common/ProjectsManagement";
import ProjectDetails from "./Components/Events/Dashboard/Common/ProjectDetails";
import MentorAssignment from "./Components/Events/Dashboard/Mentor/MentorAssignment";
import MentorOverview from "./Components/Events/Dashboard/Mentor/MentorOverview";
import MentorTeamDetails from "./Components/Events/Dashboard/Mentor/MentorTeamDetails";
import AllTeamsResourceManagement from "./Components/Events/Dashboard/Mentor/AllTeamsResourceManagement";
import ApplicationDetails from "./Components/Events/Dashboard/Common/ApplicationDetails";

// Auth Pages
import LoginPage from "./Pages/Admin/LoginPage";
import SignupPage from "./Pages/Admin/SignupPage";
import ForgotPasswordPage from "./Pages/Admin/ForgotPassword";
import EmailVerification from "./Pages/Admin/EmailVerification";

// Components
import SideNav from "./Components/Admin/SideNav";
import ProtectedRoute from "./Components/Common/ProtectedRoute";
import PublicRoute from "./Components/Common/PublicRoute";

// Legacy Components
import DashboardContent from "./Components/Admin/DashboardContent";
import ApplicationForm from "./Components/Admin/ApplicationForm";
import NotificationsPage from "./Components/Admin/Notifications";
import Users from "./Components/Admin/Users";
import ReviewDashboard from "./Components/Admin/ReviewDashboard";
import Submissions from "./Components/Admin/Submissions";
import ApplicantReview from "./Components/Admin/ApplicantReview";
import AdminDashboard from "./Components/Admin/AdminDashboard";
import Support from "./Components/Admin/Support";
import FileViewer from "./Components/Admin/FileViewer";
import DepartmentReport from "./Components/Admin/DepartmentReport";
import Inbox from "./Components/Admin/Inbox";
import Opportunities from "./Components/Admin/Opportunities";
import HackathonApplicationsList from "./Components/Admin/HackathonApplicationsList";
import HackathonApplicationDetail from "./Components/Admin/HackathonApplicationDetail";
import StakeholderSideNav from "./Components/Admin/stakeholder/StakeholderSideNav";
import StakeholderDetails from "./Components/Admin/stakeholder/StakeholderDetails";
import BasicInfo from "./Components/Admin/stakeholder/BasicInfo";
import Operations from "./Components/Admin/stakeholder/Operations";
import Sales from "./Components/Admin/stakeholder/Sales/Sales";
import HumanResource from "./Components/Admin/stakeholder/HumanResource";
import Financials from "./Components/Admin/stakeholder/Financials";
import ChargingInfrastructure from "./Components/Admin/stakeholder/ChargingInfrastructure";
import Energy from "./Components/Admin/stakeholder/Energy";
import Planning from "./Components/Admin/stakeholder/Planning";
import LandingPage from "./Components/Events/landingPage";
import EventsPage from "./Components/Events/EventsPage";
import VotingPage from "./Components/Events/voting/VotingPage";

// Pattern Background Component for Hackathon Dashboard
const PatternBackground = ({ children }) => {
  return (
    <div className="relative min-h-screen">
      {/* SVG Pattern Background */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
        style={{
          backgroundImage: `url("/Images/pattern.svg")`,
          backgroundRepeat: "repeat",
          backgroundSize: "50px 50px",
          backgroundPosition: "0 0",
        }}
      />
      {/* Content */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route
          path="/*"
          element={
            <EventLayout>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/hackathon-dashboard"
                  element={
                    <PatternBackground>
                      <PublicRoute>
                        <HackathonDashboard />
                      </PublicRoute>
                    </PatternBackground>
                  }
                />
                <Route
                  path="/my-events"
                  element={
                    <ProtectedRoute>
                      <EventsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/voting" element={<VotingPage />} />
                <Route path="/:eventId" element={<EventDetail />} />
                {/* Catch-all route for undefined routes within EventLayout - MUST BE LAST */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </EventLayout>
          }
        />

        {/* Event Dashboard Routes */}
        <Route
          path="/eventdashboard/*"
          element={
            <ProtectedRoute>
              <EventSideNav>
                <Routes>
                  <Route path="dashboard" element={<NewAdminDashboard />} />{" "}
                  {/** Admin and Judge Page */}
                  <Route
                    path="manage-applications"
                    element={<ManageApplications />}
                  />{" "}
                  {/** Admin, Judges and Mentors Page */}
                  <Route
                    path="application-details/:applicationId"
                    element={<ApplicationDetails />}
                  />
                  {/** Admin, Judges and Mentors Page */}
                  <Route
                    path="user-permissions"
                    element={<UserPermissions />}
                  />
                  {/** Admin Page */}
                  <Route
                    path="categories-judges"
                    element={<CategoriesAndJudges />}
                  />
                  {/** Admin Page */}
                  <Route
                    path="category-details"
                    element={<CategoryDetails />}
                  />
                  {/** Admin Page */}
                  <Route
                    path="projects-management"
                    element={<ProjectsManagement />}
                  />
                  {/** Admin Page */}
                  <Route
                    path="project-details/:projectId"
                    element={<ProjectDetails />}
                  />
                  {/** Admin Page */}
                  <Route
                    path="teams-management"
                    element={<TeamsManagement />}
                  />
                  {/** Admin, Judges and Mentors Page */}
                  <Route
                    path="team-details/:teamId"
                    element={<TeamDetails />}
                  />
                  {/** Admin, Judges and Mentors Page */}
                  <Route
                    path="mentor-assignment"
                    element={<MentorAssignment />}
                  />{" "}
                  {/** Admin, Judges and Mentors Page */}
                  <Route path="mentor-dashboard" element={<MentorOverview />} />
                  {/** Mentor and Admin Page */}
                  <Route
                    path="mentor-team-details/:teamId"
                    element={<MentorTeamDetails />}
                  />
                  {/** Mentor and Admin Page */}
                  <Route
                    path="all-teams-resources"
                    element={<AllTeamsResourceManagement />}
                  />
                  {/** Mentor and Admin Page */}
                </Routes>
              </EventSideNav>
            </ProtectedRoute>
          }
        />

        {/* Hackathon Application Route */}

        {/* Auth Routes with Event Parameter Support */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<SignupPage />} />
        <Route path="/forgotpassword" element={<ForgotPasswordPage />} />
        <Route path="/verify-email" element={<EmailVerification />} />

        {/* Catch-all route - redirect to home for any undefined routes */}
        <Route path="*" element={<Navigate to="/" replace />} />

        {/* Legacy Protected Routes */}
        {/* <Route
          path="/*"
          element={
            <ProtectedRoute>
              <SideNav>
                <Routes>
                  <Route index element={<DashboardContent />} />
                  <Route
                    path="application-form"
                    element={<ApplicationForm />}
                  />
                  <Route path="/update-application/:id" element={<ApplicationForm updateMode={true} />} />
                  <Route path="/department-report" element={<DepartmentReport />} />
                  <Route path="/file-viewer" element={<FileViewer />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="users" element={<Users />} />
                  <Route path="review" element={<ReviewDashboard />} />
                  <Route path="hackathon-applications/*" element={
                    <Routes>
                      <Route index element={<HackathonApplicationsList />} />
                      <Route path=":id" element={<HackathonApplicationDetail />} />
                    </Routes>
                  } />
                  <Route path="inbox" element={<Inbox />} />
                  <Route
                    path="submissions/*"
                    element={
                      <Routes>
                        <Route index element={<Submissions />} />
                        <Route path=":id" element={<ApplicantReview />} />
                      </Routes>
                    }
                  />
                  <Route path="adminsdashboard" element={<AdminDashboard />} />
                  <Route path="support" element={<Support />} />
                  <Route path="opportunities" element={<Opportunities />} />
                </Routes>
              </SideNav>
            </ProtectedRoute>
          }
        /> */}

        {/* Stakeholder Routes */}
        {/* <Route
          path="/stakeholder/:id/*"
          element={
            <ProtectedRoute>
              <StakeholderSideNav>
                <Routes>
                  <Route index element={<StakeholderDetails />} />
                  <Route path="details" element={<StakeholderDetails />} />
                  <Route path="basicinfo" element={<BasicInfo />} />
                  <Route path="operations" element={<Operations />} />
                  <Route path="sales" element={<Sales />} />
                  <Route path="humanresource" element={<HumanResource />} />
                  <Route path="financials" element={<Financials />} />
                  <Route
                    path="charginginfrastructure"
                    element={<ChargingInfrastructure />}
                  />
                  <Route path="energy" element={<Energy />} />
                  <Route path="planning" element={<Planning />} />
                </Routes>
              </StakeholderSideNav>
            </ProtectedRoute>
          }
        /> */}
      </Routes>
    </Router>
  );
}

export default App;
